import { useState, useRef, useCallback, useEffect } from 'react';
import { wsClient } from '@/lib/ws';
import { useChatStore, type ChatMessage } from '@/stores/chat.store';
import { useFileUpload, FileUploadPreview } from './file-upload';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';
import { presignVoiceMemo, completeVoiceMemo, fetchMentionSuggestions } from '@/lib/api';
import { ScheduleMessageDialog } from './schedule-message-dialog';
import { MentionAutocomplete } from './mention-autocomplete';
import { FormattingToolbar } from './formatting-toolbar';
import { PollCreator } from './poll-creator';
import { MENTION_SEARCH_DEBOUNCE_MS, SLASH_COMMANDS } from '@goodchat/shared/constants';
import { SlashCommandAutocomplete } from './slash-command-autocomplete';
import { useCloudDraft } from '@/hooks/useCloudDraft';
import { toast } from '@/stores/toast.store';
import { DRAFT_MESSAGE_SAVE_DEBOUNCE_MS } from '@goodchat/shared/constants';

interface MessageInputProps {
  areaId: string;
  orgId: string;
  threadId?: string;
  onSend: (content: string, replyToMessageId?: string, attachmentIds?: string[], voiceMemoId?: string, poll?: { question: string; options: string[]; allowMultiple: boolean; isAnonymous: boolean }) => void;
  disabled?: boolean;
  /** Callback for slash commands that require parent-level handling */
  onSlashCommand?: (command: string) => void;
  /** Externally-managed file upload (for drag-and-drop support) */
  externalFileUpload?: {
    uploads: ReturnType<typeof useFileUpload>['uploads'];
    processFiles: ReturnType<typeof useFileUpload>['processFiles'];
    retryUpload: ReturnType<typeof useFileUpload>['retryUpload'];
    removeUpload: ReturnType<typeof useFileUpload>['removeUpload'];
    clearUploads: ReturnType<typeof useFileUpload>['clearUploads'];
    getReadyAttachmentIds: ReturnType<typeof useFileUpload>['getReadyAttachmentIds'];
    hasUploads: ReturnType<typeof useFileUpload>['hasUploads'];
    allReady: ReturnType<typeof useFileUpload>['allReady'];
  };
}

interface MessageDraft { text: string }
const DEFAULT_MSG_DRAFT: MessageDraft = { text: '' };

export function MessageInput({ areaId, orgId, threadId, onSend, disabled, onSlashCommand, externalFileUpload }: MessageInputProps) {
  const { draft: msgDraft, setDraft: setMsgDraft, clearDraft: clearMsgDraft, hasDraft } = useCloudDraft<MessageDraft>(
    DEFAULT_MSG_DRAFT,
    { areaId, draftType: 'message', threadId, debounceMs: DRAFT_MESSAGE_SAVE_DEBOUNCE_MS },
  );
  const content = msgDraft.text;
  const setContent = useCallback((v: string) => setMsgDraft((prev) => ({ ...prev, text: v })), [setMsgDraft]);
  const [isSendingVoice, setIsSendingVoice] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);
  const replyingTo = useChatStore((s) => s.replyingTo[areaId]);
  const setReplyingTo = useChatStore((s) => s.setReplyingTo);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [showFormatting, setShowFormatting] = useState(false);
  const [showPollCreator, setShowPollCreator] = useState(false);
  const { isRecording, duration, startRecording, stopRecording, cancelRecording } = useVoiceRecorder();

  // Mention autocomplete state
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionSuggestions, setMentionSuggestions] = useState<Array<{ type: 'user' | 'group' | 'builtin'; id: string; displayName: string; slug: string; avatarUrl?: string | null }>>([]);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);
  const mentionDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mentionCacheRef = useRef<Map<string, typeof mentionSuggestions>>(new Map());

  // Slash command autocomplete state
  const [slashSuggestions, setSlashSuggestions] = useState<typeof SLASH_COMMANDS[number][]>([]);
  const [slashIndex, setSlashIndex] = useState(0);

  const internalFileUpload = useFileUpload();
  const {
    uploads,
    processFiles,
    retryUpload,
    removeUpload,
    clearUploads,
    getReadyAttachmentIds,
    hasUploads,
    allReady,
  } = externalFileUpload ?? internalFileUpload;

  // Sending while uploads are in flight silently dropped them (clearUploads
  // discarded the still-uploading files) — gate until everything settles.
  // Errored uploads also gate: the user must Retry or Remove them explicitly
  // rather than have them vanish from the sent message.
  const uploadsInFlight = uploads.some((u) => !u.error && u.progress < 100);
  const uploadsErrored = uploads.some((u) => u.error);
  const canSend = (content.trim() || (hasUploads && allReady)) && !uploadsInFlight && !uploadsErrored;

  const handleSend = useCallback(() => {
    const trimmed = content.trim();
    const attachmentIds = getReadyAttachmentIds();

    if (!trimmed && attachmentIds.length === 0) return;
    // Guard the Enter path too — it calls handleSend directly, bypassing canSend
    const current = externalFileUpload?.uploads ?? internalFileUpload.uploads;
    if (current.some((u) => (!u.error && u.progress < 100) || u.error)) return;

    // Sending a message is a clear user gesture — the right moment to ask for
    // notification permission (an on-load prompt gets reflexively blocked).
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }

    onSend(
      trimmed || '',
      replyingTo?.id,
      attachmentIds.length > 0 ? attachmentIds : undefined,
    );
    clearMsgDraft();
    setReplyingTo(areaId, null);
    clearUploads();
    setShowFormatting(false);

    // Stop typing
    if (isTypingRef.current) {
      wsClient.send({ event: 'typing.stop', areaId, threadId: threadId ?? null });
      isTypingRef.current = false;
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    // Re-focus
    textareaRef.current?.focus();
  }, [content, areaId, onSend, replyingTo, setReplyingTo, getReadyAttachmentIds, clearUploads, clearMsgDraft]);

  const handleSendVoice = useCallback(async () => {
    try {
      setIsSendingVoice(true);
      const { blob, durationSeconds, waveformData } = await stopRecording();

      // Get file extension based on mime type
      const ext = blob.type.includes('webm') ? 'webm' : blob.type.includes('mp4') ? 'm4a' : 'ogg';
      const fileName = `voice-memo.${ext}`;

      // 1. Presign
      const { uploadUrl, fileKey, voiceMemoId } = await presignVoiceMemo({
        fileName,
        fileType: blob.type,
        fileSize: blob.size,
        orgId,
      });

      // 2. Upload to S3
      await fetch(uploadUrl, {
        method: 'PUT',
        body: blob,
        headers: { 'Content-Type': blob.type },
      });

      // 3. Complete
      await completeVoiceMemo(voiceMemoId, {
        fileKey,
        durationSeconds,
        waveformData,
        orgId,
      });

      // 4. Send message
      onSend('', replyingTo?.id, undefined, voiceMemoId);
      setReplyingTo(areaId, null);
    } catch (err) {
      toast.error('Failed to send voice memo');
    } finally {
      setIsSendingVoice(false);
    }
  }, [stopRecording, orgId, onSend, replyingTo, setReplyingTo, areaId]);

  const handleMentionSelect = useCallback(
    (suggestion: { type: 'user' | 'group' | 'builtin'; id: string; displayName: string; slug: string; avatarUrl?: string | null }) => {
      if (mentionStart < 0) return;
      const before = content.slice(0, mentionStart);
      const after = content.slice(textareaRef.current?.selectionStart ?? content.length);
      const mentionText = suggestion.displayName.includes(' ')
        ? `@[${suggestion.displayName}]`
        : `@${suggestion.displayName}`;
      const newContent = `${before}${mentionText} ${after}`;
      setContent(newContent);
      setMentionQuery(null);
      setMentionSuggestions([]);
      setMentionStart(-1);
      setMentionIndex(0);
      // Focus and set cursor after inserted mention
      requestAnimationFrame(() => {
        const ta = textareaRef.current;
        if (ta) {
          const pos = before.length + mentionText.length + 1; // mention + space
          ta.focus();
          ta.setSelectionRange(pos, pos);
        }
      });
    },
    [content, mentionStart],
  );

  const handleSlashSelect = useCallback(
    (cmd: typeof SLASH_COMMANDS[number]) => {
      setContent('');
      setSlashSuggestions([]);
      setSlashIndex(0);
      // Handle commands that can be done inside MessageInput
      if (cmd.command === 'poll') {
        setShowPollCreator(true);
      } else if (cmd.command === 'schedule') {
        setScheduleOpen(true);
      } else {
        // Delegate to parent (status, dnd, thread)
        onSlashCommand?.(cmd.command);
      }
      textareaRef.current?.focus();
    },
    [onSlashCommand, setContent],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Slash command autocomplete keyboard nav
      if (slashSuggestions.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSlashIndex((prev) => (prev + 1) % slashSuggestions.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSlashIndex((prev) => (prev - 1 + slashSuggestions.length) % slashSuggestions.length);
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          handleSlashSelect(slashSuggestions[slashIndex]);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setSlashSuggestions([]);
          setSlashIndex(0);
          return;
        }
      }

      // Mention autocomplete keyboard nav
      if (mentionSuggestions.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setMentionIndex((prev) => (prev + 1) % mentionSuggestions.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setMentionIndex((prev) => (prev - 1 + mentionSuggestions.length) % mentionSuggestions.length);
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          handleMentionSelect(mentionSuggestions[mentionIndex]);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setMentionQuery(null);
          setMentionSuggestions([]);
          setMentionStart(-1);
          return;
        }
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        // IME composition (Japanese/Chinese/Korean input, some mobile
        // keyboards): Enter confirms the candidate, it must not send.
        if (e.nativeEvent.isComposing) return;
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend, mentionSuggestions, mentionIndex, handleMentionSelect, slashSuggestions, slashIndex, handleSlashSelect],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setContent(value);

      // Slash command detection: only when entire input is /command (nothing else)
      const slashMatch = value.match(/^\/(\S*)$/);
      if (slashMatch) {
        const q = slashMatch[1].toLowerCase();
        const filtered = SLASH_COMMANDS.filter((c) => c.command.startsWith(q));
        setSlashSuggestions(filtered);
        setSlashIndex(0);
      } else {
        if (slashSuggestions.length > 0) {
          setSlashSuggestions([]);
          setSlashIndex(0);
        }
      }

      // Mention detection: find @ before cursor
      const cursorPos = e.target.selectionStart;
      const textBeforeCursor = value.slice(0, cursorPos);
      const mentionMatch = textBeforeCursor.match(/@(\S*)$/);

      if (mentionMatch) {
        const q = mentionMatch[1];
        const start = textBeforeCursor.length - mentionMatch[0].length;
        setMentionStart(start);
        setMentionQuery(q);
        setMentionIndex(0);

        // Debounced fetch with cache
        if (mentionDebounceRef.current) clearTimeout(mentionDebounceRef.current);
        const cacheKey = `${orgId}:${q}`;
        const cached = mentionCacheRef.current.get(cacheKey);
        if (cached) {
          setMentionSuggestions(cached);
        } else {
          mentionDebounceRef.current = setTimeout(() => {
            fetchMentionSuggestions(orgId, q)
              .then((res) => {
                mentionCacheRef.current.set(cacheKey, res.suggestions);
                setMentionSuggestions(res.suggestions);
              })
              .catch(() => setMentionSuggestions([]));
          }, MENTION_SEARCH_DEBOUNCE_MS);
        }
      } else {
        setMentionQuery(null);
        setMentionSuggestions([]);
        setMentionStart(-1);
      }

      // Typing indicator
      if (!isTypingRef.current) {
        isTypingRef.current = true;
        wsClient.send({ event: 'typing.start', areaId, threadId: threadId ?? null });
      }

      // Reset stop timer
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        if (isTypingRef.current) {
          wsClient.send({ event: 'typing.stop', areaId, threadId: threadId ?? null });
          isTypingRef.current = false;
        }
      }, 3000);
    },
    [areaId, orgId],
  );

  // Handle paste with image data
  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles: File[] = [];
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) imageFiles.push(file);
        }
      }

      if (imageFiles.length > 0) {
        e.preventDefault();
        processFiles(imageFiles);
      }
    },
    [processFiles],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
      // Reset input so same file can be selected again
      e.target.value = '';
    },
    [processFiles],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (isTypingRef.current) {
        wsClient.send({ event: 'typing.stop', areaId, threadId: threadId ?? null });
      }
    };
  }, [areaId]);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
    }
  }, [content]);

  // Recording UI
  if (isRecording || isSendingVoice) {
    return (
      <div className="border-t border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-center gap-3 px-4 py-3">
          {/* Red pulsing dot + recording label */}
          <div className="flex items-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500" />
            </span>
            <span className="text-sm font-medium text-red-600">
              {isSendingVoice ? 'Sending...' : `Recording... ${formatDuration(duration)}`}
            </span>
          </div>

          <div className="flex-1" />

          {/* Cancel button */}
          <button
            onClick={cancelRecording}
            disabled={isSendingVoice}
            className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-800"
            title="Cancel"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Send voice memo button */}
          <button
            onClick={handleSendVoice}
            disabled={isSendingVoice}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            title="Send voice memo"
          >
            {isSendingVoice ? (
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
      {/* Attachment count badge */}
      {hasUploads && (
        <div className="flex items-center gap-1.5 px-4 pt-2">
          <svg className="h-3.5 w-3.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
          <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
            {uploads.length} file{uploads.length === 1 ? '' : 's'} attached
          </span>
        </div>
      )}

      {/* Upload preview strip */}
      <FileUploadPreview uploads={uploads} onRemove={removeUpload} onRetry={retryUpload} />

      {/* Reply preview */}
      {replyingTo && (
        <div className="mx-4 mt-2 flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 dark:bg-gray-800">
          <div className="flex-1 overflow-hidden">
            <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
              Replying to {replyingTo.author.displayName}
            </span>
            <p className="truncate text-xs text-gray-500 dark:text-gray-400">
              {replyingTo.content}
            </p>
          </div>
          <button
            onClick={() => setReplyingTo(areaId, null)}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Cancel reply"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Poll creator (opt-in) */}
      {showPollCreator && (
        <div className="px-4 pt-2">
          <PollCreator
            onSubmit={(pollData) => {
              onSend('', replyingTo?.id, undefined, undefined, pollData);
              setShowPollCreator(false);
              setReplyingTo(areaId, null);
            }}
            onCancel={() => setShowPollCreator(false)}
          />
        </div>
      )}

      {/* Formatting toolbar (opt-in) */}
      {showFormatting && (
        <FormattingToolbar textareaRef={textareaRef} content={content} setContent={setContent} />
      )}

      {/* Draft restored indicator */}
      {hasDraft && (
        <div className="flex items-center gap-1.5 px-4 pt-1.5">
          <svg className="h-3 w-3 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
          </svg>
          <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Draft</span>
        </div>
      )}

      <div className="relative flex items-end gap-2 px-4 py-3" role="toolbar" aria-label="Message input">
        {slashSuggestions.length > 0 ? (
          <SlashCommandAutocomplete
            commands={slashSuggestions}
            selectedIndex={slashIndex}
            onSelect={handleSlashSelect}
          />
        ) : mentionSuggestions.length > 0 ? (
          <MentionAutocomplete
            suggestions={mentionSuggestions}
            selectedIndex={mentionIndex}
            onSelect={handleMentionSelect}
          />
        ) : null}
        {/* File upload button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          aria-label="Attach file"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*,audio/*"
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Mic button */}
        <button
          onClick={startRecording}
          disabled={disabled}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          aria-label="Record voice memo"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4M12 15a3 3 0 003-3V5a3 3 0 00-6 0v7a3 3 0 003 3z" />
          </svg>
        </button>

        {/* Poll creator button */}
        {/* Hidden on a phone: at 375px this row's six 36px buttons plus its
              padding left the textarea 79 CSS pixels — unusable in a chat app.
              These three are the ones with another way in: /poll and /schedule
              are slash commands, and markdown can be typed by hand. */}
        <button
          onClick={() => setShowPollCreator((prev) => !prev)}
          disabled={disabled}
          className={`hidden sm:flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg transition-colors disabled:opacity-50 ${
            showPollCreator
              ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400'
              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300'
          }`}
          aria-label="Create poll"
          title="Create poll"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </button>

        {/* Formatting toggle button */}
        <button
          onClick={() => setShowFormatting((prev) => !prev)}
          disabled={disabled}
          className={`hidden sm:flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-sm font-bold transition-colors disabled:opacity-50 ${
            showFormatting
              ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400'
              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300'
          }`}
          aria-label="Toggle formatting toolbar"
          title="Formatting"
        >
          Aa
        </button>

        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder="Type a message..."
          disabled={disabled}
          className="flex-1 resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none transition-colors placeholder:text-gray-400 focus:border-blue-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:border-blue-400"
          rows={1}
        />
        {/* Schedule button */}
        <button
          onClick={() => { if (content.trim()) setScheduleOpen(true); }}
          disabled={disabled || !content.trim()}
          className="hidden sm:flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          aria-label="Schedule message"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
        {/* Start thread affordance (DESIGN.md: threads get an explicit entry
            point in the composer, distinct from sending a message) */}
        {onSlashCommand && !threadId && (
          <button
            onClick={() => onSlashCommand('/thread')}
            disabled={disabled}
            className="hidden h-9 flex-shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-50 disabled:opacity-50 sm:flex dark:text-blue-300 dark:hover:bg-blue-900/30"
            aria-label="Start a thread"
            title="Start a named thread — a topic the team can resolve"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
            Start thread
          </button>
        )}
        <button
          onClick={handleSend}
          disabled={disabled || !canSend}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:hover:bg-blue-600"
          aria-label="Send message"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </button>
      </div>
      <ScheduleMessageDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        areaId={areaId}
        content={content}
        onScheduled={() => {
          setContent('');
        }}
      />
    </div>
  );
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

